<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useAuth } from '@/composables/useAuth'
import { useNewsletters } from '@/composables/useNewsletters'
import { storeNewsletter, destroyNewsletter } from '@/api/newsletters'
import OrganizerLayout from '@/components/layout/OrganizerLayout.vue'
import ConfirmDialog from '@/components/shared/ConfirmDialog.vue'
import OverflowMenu from '@/components/shared/OverflowMenu.vue'
import HeaderCreateButton from '@/components/shared/HeaderCreateButton.vue'
import TabStrip from '@/components/shared/TabStrip.vue'
import type { Newsletter, NewsletterStatus } from '@/types/newsletter'
import { NewsletterStatus as NStatus, NewsletterType } from '@/types/newsletter'

function isAxiosError(e: unknown): e is { response: { status: number } } {
  return typeof e === 'object' && e !== null && 'response' in e
}

const { t } = useI18n()

const router = useRouter()
const { currentOrganisation } = useAuth()

const orgId = computed<number | null>(() => currentOrganisation.value?.id ?? null)

// Tabs
type TabKey = 'all' | 'draft' | 'scheduled' | 'sent'
const tabs: Array<{ key: TabKey; label: string }> = [
  { key: 'all',       label: t('newsletters.list.tab_all') },
  { key: 'draft',     label: t('newsletters.list.tab_drafts') },
  { key: 'scheduled', label: t('newsletters.list.tab_scheduled') },
  { key: 'sent',      label: t('newsletters.list.tab_sent') },
]
const activeTab = ref<TabKey>('all')

const { newsletters, loading, error, fetch } = useNewsletters(orgId.value ?? 0)

async function loadTab(tab: TabKey): Promise<void> {
  activeTab.value = tab
  if (!orgId.value) return
  await fetch(tab === 'all' ? 'all' : (tab as NewsletterStatus))
}

onMounted(async () => {
  await loadTab('all')
})

// Delete confirmation
const confirmDeleteId = ref<number | null>(null)
const confirmDeleteTitle = ref('')
const deleteLoading = ref(false)
const deleteErrorMessage = ref('')

function requestDelete(nl: Newsletter): void {
  confirmDeleteId.value = nl.id
  confirmDeleteTitle.value = nl.title
  deleteErrorMessage.value = ''
}

function cancelDelete(): void {
  confirmDeleteId.value = null
  confirmDeleteTitle.value = ''
  deleteErrorMessage.value = ''
}

async function confirmDelete(): Promise<void> {
  if (!confirmDeleteId.value || !orgId.value) return
  deleteLoading.value = true
  try {
    await destroyNewsletter(orgId.value, confirmDeleteId.value)
    confirmDeleteId.value = null
    confirmDeleteTitle.value = ''
    await loadTab(activeTab.value)
  } catch (e) {
    confirmDeleteId.value = null
    confirmDeleteTitle.value = ''
    if (isAxiosError(e) && e.response.status === 409) {
      deleteErrorMessage.value = t('newsletters.list.delete_conflict_error')
    } else {
      deleteErrorMessage.value = t('common.error_generic')
    }
  } finally {
    deleteLoading.value = false
  }
}

// Create new newsletter
const creating = ref(false)

async function createNewsletter(): Promise<void> {
  if (!orgId.value) return
  creating.value = true
  try {
    const nl = await storeNewsletter(orgId.value, {
      title: 'Untitled newsletter',
      type: NewsletterType.Newsletter,
      subject: '',
      body: '',
    })
    const slug = currentOrganisation.value?.slug
    if (slug) {
      void router.push(`/organizer/organisations/${slug}/newsletters/${nl.id}`)
    }
  } finally {
    creating.value = false
  }
}

// Formatting helpers
function statusLabel(status: NewsletterStatus): string {
  const map: Record<NewsletterStatus, string> = {
    draft:     t('newsletters.status.draft'),
    scheduled: t('newsletters.status.scheduled'),
    sending:   t('newsletters.status.sending'),
    sent:      t('newsletters.status.sent'),
    failed:    t('newsletters.status.failed'),
  }
  return map[status] ?? status
}

function typeLabel(type: string): string {
  const map: Record<string, string> = {
    briefing:   t('newsletters.type.briefing'),
    debriefing: t('newsletters.type.debriefing'),
    newsletter: t('newsletters.type.newsletter'),
    race_update: t('newsletters.type.race_update'),
  }
  return map[type] ?? type
}

function formatDateTime(iso: string | null): string {
  if (!iso) return ''
  return new Date(iso).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function formatDate(iso: string | null): string {
  if (!iso) return ''
  return new Date(iso).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

function editPath(nl: Newsletter): string {
  const slug = currentOrganisation.value?.slug
  return slug ? `/organizer/organisations/${slug}/newsletters/${nl.id}` : ''
}
</script>

<template>
  <OrganizerLayout>
    <main class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <!-- Page heading -->
      <div class="flex items-center justify-between mb-8">
        <div>
          <h1 class="text-3xl font-bold text-white">{{ t('newsletters.list.heading') }}</h1>
          <p class="mt-1 text-slate-400">{{ t('newsletters.list.subheading') }}</p>
        </div>
        <HeaderCreateButton
          :disabled="creating"
          :label="t('newsletters.list.create_button')"
          @click="createNewsletter"
        />
      </div>

      <!-- Error -->
      <div
        v-if="error"
        class="mb-6 rounded-xl bg-red-500/10 border border-red-500/30 px-5 py-3 text-sm text-red-400"
      >
        {{ error }}
      </div>

      <!-- Delete error -->
      <div
        v-if="deleteErrorMessage"
        class="mb-6 rounded-xl bg-red-500/10 border border-red-500/30 px-5 py-3 text-sm text-red-400"
      >
        {{ deleteErrorMessage }}
      </div>

      <!-- Tab strip -->
      <TabStrip
        :model-value="activeTab"
        :tabs="tabs"
        class="mb-6"
        @update:model-value="loadTab($event as TabKey)"
      />

      <!-- Skeleton loading state -->
      <div v-if="loading" class="space-y-3">
        <div
          v-for="i in 4"
          :key="i"
          class="rounded-xl bg-slate-800/50 border border-slate-700 px-5 py-4 animate-pulse"
        >
          <div class="flex items-start gap-4">
            <div class="flex-1 space-y-2">
              <div class="h-4 bg-slate-700 rounded w-64" />
              <div class="h-3 bg-slate-700 rounded w-80" />
              <div class="flex gap-2 mt-2">
                <div class="h-5 bg-slate-700 rounded-full w-20" />
                <div class="h-5 bg-slate-700 rounded-full w-24" />
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Empty state -->
      <div
        v-else-if="newsletters.length === 0"
        class="rounded-2xl border border-dashed border-slate-700 bg-slate-800/30 px-8 py-16 text-center"
      >
        <div class="mx-auto mb-4 w-14 h-14 rounded-full bg-slate-800 flex items-center justify-center">
          <svg class="w-7 h-7 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
          </svg>
        </div>
        <p class="text-slate-400 text-sm mb-6">
          <span v-if="activeTab === 'all'">{{ t('newsletters.list.empty_all') }}</span>
          <span v-else-if="activeTab === 'draft'">{{ t('newsletters.list.empty_drafts') }}</span>
          <span v-else-if="activeTab === 'scheduled'">{{ t('newsletters.list.empty_scheduled') }}</span>
          <span v-else>{{ t('newsletters.list.empty_sent') }}</span>
        </p>
        <button
          v-if="activeTab === 'all'"
          type="button"
          :disabled="creating"
          class="inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-400 disabled:opacity-50 text-white font-semibold rounded-lg px-5 py-2.5 text-sm transition-colors"
          @click="createNewsletter"
        >
          {{ t('newsletters.list.create_first_button') }}
        </button>
      </div>

      <!-- Newsletter card list -->
      <div v-else class="space-y-3">
        <article
          v-for="nl in newsletters"
          :key="nl.id"
          class="rounded-xl bg-slate-800/50 border border-slate-700 px-5 py-4 hover:border-slate-600 transition-colors"
        >
          <div class="flex items-start gap-4">
            <div class="flex-1 min-w-0">
              <!-- Title row -->
              <div class="flex items-center gap-2 flex-wrap">
                <RouterLink
                  :to="editPath(nl)"
                  class="text-sm font-semibold text-white hover:text-emerald-400 transition-colors"
                >
                  {{ nl.title }}
                </RouterLink>
                <!-- Type badge -->
                <span class="shrink-0 inline-flex items-center rounded-full bg-slate-700/80 px-2 py-0.5 text-xs font-medium text-slate-300">
                  {{ typeLabel(nl.type) }}
                </span>
                <!-- Status pill -->
                <span
                  class="shrink-0 inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium border"
                  :class="{
                    'bg-slate-700/50 border-slate-600 text-slate-400':       nl.status === NStatus.Draft,
                    'bg-amber-500/15 border-amber-500/30 text-amber-400':    nl.status === NStatus.Scheduled,
                    'bg-sky-500/15 border-sky-500/30 text-sky-400':          nl.status === NStatus.Sending,
                    'bg-emerald-500/15 border-emerald-500/30 text-emerald-400': nl.status === NStatus.Sent,
                    'bg-red-500/15 border-red-500/30 text-red-400':          nl.status === NStatus.Failed,
                  }"
                >
                  <span
                    v-if="nl.status === NStatus.Sending"
                    class="w-1.5 h-1.5 rounded-full bg-sky-400 mr-1 animate-pulse"
                    aria-hidden="true"
                  />
                  {{ statusLabel(nl.status) }}
                </span>
              </div>

              <!-- Subject preview -->
              <p class="text-xs text-slate-400 mt-1 truncate">
                <span v-if="nl.subject">{{ nl.subject }}</span>
                <span v-else class="italic">{{ t('newsletters.list.no_subject') }}</span>
              </p>

              <!-- Meta line: varies by status -->
              <div class="mt-1.5 text-xs text-slate-500">
                <span v-if="nl.status === NStatus.Draft">
                  {{ t('newsletters.list.last_edited', { date: formatDate(nl.updated_at) }) }}
                </span>
                <span v-else-if="nl.status === NStatus.Scheduled && nl.scheduled_at">
                  {{ t('newsletters.list.sends_at', { datetime: formatDateTime(nl.scheduled_at) }) }}
                  <template v-if="nl.recipient_count !== null">
                    &middot; {{ t('newsletters.list.recipient_count', { count: nl.recipient_count.toLocaleString() }) }}
                  </template>
                </span>
                <span v-else-if="nl.status === NStatus.Sent && nl.sent_at">
                  {{ t('newsletters.list.sent_on', { date: formatDate(nl.sent_at) }) }}
                  <template v-if="nl.recipient_count !== null">
                    &middot; {{ t('newsletters.list.recipient_count', { count: nl.recipient_count.toLocaleString() }) }}
                  </template>
                </span>
                <span v-else-if="nl.status === NStatus.Failed">
                  {{ t('newsletters.status.failed') }} · {{ nl.failure_reason ?? t('newsletters.list.failure_unknown') }}
                </span>
              </div>
            </div>

            <!-- Right: actions -->
            <div class="shrink-0 flex items-center gap-2">
              <RouterLink
                :to="editPath(nl)"
                class="hidden sm:inline-flex text-xs font-medium text-emerald-400 hover:text-emerald-300 transition-colors px-3 py-1.5 rounded-lg border border-emerald-500/30 hover:border-emerald-500/60"
              >
                {{ t('newsletters.list.action_edit') }}
              </RouterLink>

              <!-- Overflow menu (Edit is already the first item here, so no extra
                   mobile-only duplicate is needed for it below sm) -->
              <OverflowMenu
                menu-class="w-44"
                :aria-label="t('newsletters.list.more_actions_label', { title: nl.title })"
              >
                <RouterLink
                  :to="editPath(nl)"
                  class="w-full text-left px-4 py-2 text-sm text-slate-300 hover:text-white hover:bg-slate-700/50 transition-colors flex items-center"
                >
                  {{ t('newsletters.list.action_edit') }}
                </RouterLink>
                <hr class="border-slate-700 my-1" />
                <button
                  v-if="nl.status === NStatus.Scheduled"
                  type="button"
                  class="w-full text-left px-4 py-2 text-sm text-amber-400 hover:text-amber-300 hover:bg-slate-700/50 transition-colors"
                  @click="requestDelete(nl)"
                >
                  {{ t('newsletters.list.action_cancel_send') }}
                </button>
                <button
                  v-if="nl.status === NStatus.Draft || nl.status === NStatus.Failed"
                  type="button"
                  class="w-full text-left px-4 py-2 text-sm text-red-400 hover:text-red-300 hover:bg-slate-700/50 transition-colors"
                  @click="requestDelete(nl)"
                >
                  {{ t('newsletters.list.action_delete') }}
                </button>
              </OverflowMenu>
            </div>
          </div>
        </article>
      </div>
    </main>

    <ConfirmDialog
      :open="confirmDeleteId !== null"
      :title="t('newsletters.list.delete_dialog_title')"
      :body="t('newsletters.list.delete_dialog_body', { title: confirmDeleteTitle })"
      :confirm-label="t('newsletters.list.delete_confirm')"
      :loading="deleteLoading"
      @update:open="cancelDelete"
      @confirm="confirmDelete"
    />
  </OrganizerLayout>
</template>
